import mergeClassNames from "common/mergeClassNames";

type MessageVariant = "default" | "info" | "warning" | "error";
type MessageSize = "mini" | "tiny" | "small" | "large" | "big" | "huge" | "massive";

interface MessageBoxProps {
    type?: MessageVariant;
    negative?: boolean;
    positive?: boolean;
    floating?: boolean;
    size?: MessageSize;
    header: string;
    children: React.ReactNode;
    className?: string;
}

const VARIANT_STYLES: Record<MessageVariant, { container: string; header: string; body: string }> = {
    default: {
        container: "bg-[#f4f4f4] shadow-[inset_0_0_0_1px_#d4d4d4]",
        header: "text-[#1b1c1d]",
        body: "text-[#272729]",
    },
    info: {
        container: "bg-[#f8ffff] shadow-[inset_0_0_0_1px_#a9d5de]",
        header: "text-[#0e566c]",
        body: "text-[#468498]",
    },
    warning: {
        container: "bg-[#fffaf3] shadow-[inset_0_0_0_1px_#c9ba9b]",
        header: "text-[#573a08]",
        body: "text-[#b58105]",
    },
    error: {
        container: "bg-[#fff6f6] shadow-[inset_0_0_0_1px_#e0b4b4]",
        header: "text-[#912d2b]",
        body: "text-[#9f3a38]",
    },
};

const SIZE_STYLES: Record<MessageSize, { container: string; header: string; body: string }> = {
    mini: { container: "text-[0.78571429rem]", header: "text-[0.85714286em]", body: "text-[1em]" },
    tiny: { container: "text-[0.85714286rem]", header: "text-[0.85714286em]", body: "text-[1em]" },
    small: { container: "text-[0.92857143rem]", header: "text-[0.85714286em]", body: "text-[1em]" },
    large: { container: "text-[1.14285714rem]", header: "text-[1.14285714em]", body: "text-[1em]" },
    big: { container: "text-[1.28571429rem]", header: "text-[1.14285714em]", body: "text-[1em]" },
    huge: { container: "text-[1.42857143rem]", header: "text-[1.14285714em]", body: "text-[1em]" },
    massive: { container: "text-[1.71428571rem]", header: "text-[1.14285714em]", body: "text-[1em]" },
};

export const MessageBox: React.FC<MessageBoxProps> = ({
    type = "default",
    negative,
    positive,
    floating,
    size,
    header,
    children,
    className,
}) => {
    let messageType = type;
    if (negative) messageType = "error";
    if (positive) messageType = "info";

    const styles = VARIANT_STYLES[messageType];
    const sizeStyles = size ? SIZE_STYLES[size] : null;

    const floatingStyles = floating ? "shadow-[0_0_0_1px_rgba(34,36,38,.22)_inset,0_2px_4px_0_rgba(34,36,38,.12),0_2px_10px_0_rgba(34,36,38,.15)]" : "";

    return (
        <div className={mergeClassNames("w-auto", className)}>
            <div
                className={mergeClassNames(
                    "rounded-[0.29em] px-[1.5em] py-[1em]",
                    "font-sans",
                    styles.container,
                    floatingStyles,
                    sizeStyles?.container
                )}
            >
                <h2 className={mergeClassNames("mb-1 font-semibold", "text-[1.15em]", styles.header, sizeStyles?.header)}>{header}</h2>
                <div className={mergeClassNames("text-[1em]", styles.body, sizeStyles?.body)}>{children}</div>
            </div>
        </div>
    );
};
